import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, ArrowUpRight, CheckCircle2, Clock, Loader2, Copy, Check, RefreshCw, Mail, KeyRound, Zap } from "lucide-react";
import InstructionModal from "@/components/InstructionModal";

const OTP_TTL = 60;

// ── Avatar catalogue ─────────────────────────────────────────────────────────
const AVATAR_OPTIONS = {
  F: [
    { id: "24-girl", label: "Avani",   img: "/24-girl.png" },
    { id: "25-girl", label: "Priya",   img: "/25-girl.png" },
    { id: "26-girl", label: "Diya",    img: "/26-girl.png" },
    { id: "34-girl", label: "Nisha",   img: "/34-girl.png" },
    { id: "35-girl", label: "Sana",    img: "/35-girl.png" },
    { id: "36-girl", label: "Riya",    img: "/36-girl.png" },
    { id: "38-girl", label: "Kavya",   img: "/38-girl.png" },
  ],
  M: [
    { id: "27-boy",  label: "Arjun",   img: "/27-boy.png"  },
    { id: "28-boy",  label: "Vikram",  img: "/28-boy.png"  },
    { id: "29-boy",  label: "Rohan",   img: "/29-boy.png"  },
    { id: "30-boy",  label: "Kiran",   img: "/30-boy.png"  },
    { id: "31-boy",  label: "Dev",     img: "/31-boy.png"  },
    { id: "32-boy",  label: "Aakash",  img: "/32-boy.png"  },
    { id: "33-boy",  label: "Raj",     img: "/33-boy.png"  },
    { id: "37-boy",  label: "Nikhil",  img: "/37-boy.png"  },
  ],
};
const DEFAULT_AVATAR = { id: "27-boy", label: "Champion", img: "/27-boy.png" };

function getRandomAvatar(gender) {
  const pool = AVATAR_OPTIONS[gender] || AVATAR_OPTIONS.M;
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * 2-Player side-by-side battle registration.
 * Each player flow is independent:
 *  form -> otp -> ready (verified) -> joined (waiting in queue) -> matched (navigate)
 * The FIRST player to be matched routes the whole modal to /battle (since both
 * players at the same booth match each other almost instantly).
 */
function PlayerForm({ idx, player, setPlayer, status, setStatus, joinId, setJoinId, onMatch, otherPlayerPhone, onMatchNumChange }) {
  const [otp, setOtp] = useState(["", "", "", "", ""]);
  const [devOtp, setDevOtp] = useState(null);
  const [entryCode, setEntryCode] = useState(null);
  const [entryCopied, setEntryCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [joining, setJoining] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [countdown, setCountdown] = useState(OTP_TTL);
  const [copied, setCopied] = useState(false);
  // Battle OTP quick-login
  const [battleOtp, setBattleOtp] = useState(["", "", "", "", ""]);
  const [logging, setLogging] = useState(false);
  const boRefs = useRef([]);
  // "lost entry code" resend flow
  const [showResend, setShowResend] = useState(false);
  const [resendPhone, setResendPhone] = useState("");
  const [resending, setResending] = useState(false);
  const [resendDone, setResendDone] = useState(null);
  // Avatar selection
  const [gender, setGender] = useState(null); // "M" | "F"
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const refs = useRef([]);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  const startCountdown = useCallback(() => {
    setCountdown(OTP_TTL);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => { if (c <= 1) { clearInterval(timerRef.current); return 0; } return c - 1; });
    }, 1000);
  }, []);

  useEffect(() => () => { clearInterval(timerRef.current); }, []);

  // poll queue once we're waiting
  useEffect(() => {
    if (status !== "waiting" || !joinId) return;
    setElapsed(0);
    const ticker = setInterval(() => setElapsed((e) => e + 1), 1000);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.battleQueueStatus(joinId);
        if (r.status === "matched") {
          clearInterval(pollRef.current);
          clearInterval(ticker);
          setStatus("matched");
          onMatch(r, selectedAvatar || getRandomAvatar(gender));
        }
      } catch { /* keep polling */ }
    }, 2500);
    return () => { clearInterval(pollRef.current); clearInterval(ticker); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, joinId]);

  const resetToForm = () => {
    setStatus("battle-otp");
    setBattleOtp(["", "", "", "", ""]);
    setOtp(["", "", "", "", ""]);
    setDevOtp(null);
    setEntryCode(null);
    setJoinId(null);
  };

  const handleBoChange = (i, v) => {
    const ch = v.replace(/\D/g, "").slice(-1);
    const next = [...battleOtp]; next[i] = ch; setBattleOtp(next);
    if (ch && i < 4) boRefs.current[i + 1]?.focus();
  };
  const handleBoKey = (i, e) => { if (e.key === "Backspace" && !battleOtp[i] && i > 0) boRefs.current[i - 1]?.focus(); };
  const handleBoPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 5);
    if (!txt) return; e.preventDefault();
    const arr = ["", "", "", "", ""];
    for (let i = 0; i < txt.length; i++) arr[i] = txt[i];
    setBattleOtp(arr);
    boRefs.current[Math.min(txt.length, 4)]?.focus();
  };

  const loginWithBattleOtp = async () => {
    const code = battleOtp.join("");
    if (code.length < 5) { toast.error(`Player ${idx}: enter all 5 digits`); return; }
    setLogging(true);
    try {
      const r = await api.userQuickLogin(code);
      const u = { name: r.name, email: r.email, phone: r.phone };
      // Prevent both players using same account
      const clean = (x) => (x || "").replace(/\D/g, "");
      if (otherPlayerPhone && clean(u.phone) === clean(otherPlayerPhone)) {
        toast.error("Both players cannot use the same account."); setLogging(false); return;
      }
      setPlayer(u);
      toast.success(`Player ${idx}: Welcome back, ${r.name.split(" ")[0]}!`);
      setStatus("avatar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP not recognised. Register first.");
    } finally { setLogging(false); }
  };

  const send = async () => {
    if (!player.name || !player.email || !player.phone) {
      toast.error(`Player ${idx}: fill all fields`); return;
    }
    // Prevent both players using the same phone number
    const clean = (p) => (p || "").replace(/\D/g, "");
    if (otherPlayerPhone && clean(player.phone) && clean(player.phone) === clean(otherPlayerPhone)) {
      toast.error("Both players cannot use the same phone number. Please use a different number.");
      return;
    }
    setSending(true);
    try {
      const r = await api.otpSend(player);
      const code = r.dev_otp || null;
      setDevOtp(code);
      if (code) {
        const arr = code.split("").slice(0, 5);
        while (arr.length < 5) arr.push("");
        setOtp(arr);
      }
      startCountdown();
      setStatus("otp");
      setTimeout(() => refs.current[4]?.focus(), 80);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP failed");
    } finally { setSending(false); }
  };

  const resendOtp = async () => {
    if (countdown > 0) return;
    setSending(true);
    try {
      const r = await api.otpSend(player);
      const code = r.dev_otp || null;
      setDevOtp(code);
      if (code) {
        const arr = code.split("").slice(0, 5);
        while (arr.length < 5) arr.push("");
        setOtp(arr);
      }
      startCountdown();
    } catch (e) { toast.error("Failed to regenerate OTP"); }
    finally { setSending(false); }
  };

  const copyOtp = () => {
    if (!devOtp) return;
    navigator.clipboard.writeText(devOtp).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const handleChange = (i, v) => {
    const ch = v.replace(/\D/g, "").slice(-1);
    const next = [...otp]; next[i] = ch; setOtp(next);
    if (ch && i < 4) refs.current[i + 1]?.focus();
  };
  const handlePaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 5);
    if (!txt) return; e.preventDefault();
    const arr = ["", "", "", "", ""];
    for (let i = 0; i < txt.length; i++) arr[i] = txt[i];
    setOtp(arr);
  };

  const verify = async () => {
    const code = otp.join("");
    if (code.length < 5) { toast.error(`Player ${idx}: enter 5 digits`); return; }
    setVerifying(true);
    try {
      const r = await api.otpVerify({ ...player, otp: code });
      toast.success(`Player ${idx} verified!`);
      if (r.entry_code) setEntryCode(r.entry_code);
      // carry gender from form into avatar step
      if (player.gender) setGender(player.gender);
      setStatus("avatar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid OTP");
    } finally { setVerifying(false); }
  };

  const copyEntryCode = () => {
    if (!entryCode) return;
    navigator.clipboard.writeText(entryCode).then(() => {
      setEntryCopied(true);
      setTimeout(() => setEntryCopied(false), 2000);
    });
  };

  const resendEntryCode = async () => {
    if (!resendPhone.trim()) { toast.error("Enter your registered phone number"); return; }
    setResending(true);
    try {
      const r = await api.userResendBattleEntry(resendPhone.trim());
      setResendDone({ email: r.email, devCode: r.dev_code });
      toast.success(r.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend — please re-register");
    } finally { setResending(false); }
  };

  const timerColor = countdown > 30 ? "text-emerald-400" : countdown > 10 ? "text-yellow-400" : "text-[color:var(--kl-red)]";
  const timerBg = countdown > 30 ? "bg-emerald-500/10 border-emerald-500/30" : countdown > 10 ? "bg-yellow-500/10 border-yellow-500/30" : "bg-red-500/10 border-red-500/30";

  // Tournament self-register + poll for admin start
  const [matchInfo, setMatchInfo] = useState(null); // { match_num, opponent_name, tournament_name }

  const join = async () => {
    const avatar = selectedAvatar || getRandomAvatar(gender);
    setSelectedAvatar(avatar);
    setPlayer((prev) => ({ ...prev, avatar }));
    setJoining(true);
    try {
      const r = await api.tournamentSelfRegister({
        name: player.name,
        email: player.email,
        phone: player.phone,
        gender: gender || "",
        avatar: avatar || {},
        entry_code: entryCode || "",
      });
      setMatchInfo({
        match_num: r.match_num,
        opponent_name: r.opponent_name,
        tournament_name: r.tournament_name,
        tournament_id: r.tournament_id,
        status: r.status,
        room_id: r.room_id,
      });
      if (r.status === "admin_started" && r.battle_info) {
        // already started — go straight to battle
        onMatch({ ...r.battle_info, tournament: { id: r.tournament_id, name: r.tournament_name } }, avatar);
        setStatus("matched");
      } else {
        // waiting_for_players (P1 waiting for P2) or pending (both assigned, waiting admin)
        setStatus("waiting_admin");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to register";
      toast.error(msg);
    } finally { setJoining(false); }
  };

  // Poll for match update + admin start when waiting
  useEffect(() => {
    if (status !== "waiting_admin" || !player.phone) return;
    const tick = async () => {
      try {
        const r = await api.tournamentMyMatchStatus(player.phone);
        // Always sync match info so match number/opponent appear as soon as paired
        setMatchInfo((prev) => ({ ...prev, ...r, tournament_live: r.tournament_live ?? prev?.tournament_live ?? false }));
        if (r.match_num) onMatchNumChange?.(r.match_num);
        if (r.status === "admin_started" && r.room_id && r.battle_info) {
          clearInterval(pollRef.current);
          const avatar = selectedAvatar || getRandomAvatar(gender);
          onMatch({
            ...r.battle_info,
            tournament: { id: r.tournament_id, name: r.tournament_name },
          }, avatar);
          setStatus("matched");
        }
      } catch { /* keep polling */ }
    };
    tick(); // immediate first fetch — no waiting for interval
    pollRef.current = setInterval(tick, 1500);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, player.phone]);

  return (
    <div
      className="relative rounded-md border border-[color:var(--kl-navy-line)] p-6 md:p-7 bg-white/[0.02]"
      data-testid={`battle-form-p${idx}`}
      style={(status === "avatar" || status === "ready" || status === "waiting" || status === "waiting_admin" || status === "matched") ? { borderColor: "var(--kl-red)", background: "rgba(230,57,70,0.06)" } : {}}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-mono-kl text-[11px] uppercase tracking-[0.2em] text-white/50">Player</span>
          <span className="font-display text-2xl text-[color:var(--kl-red)]">0{idx}</span>
        </div>
        {(status === "avatar" || status === "ready" || status === "waiting" || status === "waiting_admin" || status === "matched") && (
          <div className="flex items-center gap-1.5 text-[color:var(--kl-red)] font-mono-kl text-[10px] uppercase tracking-widest">
            <CheckCircle2 className="w-4 h-4" /> Verified
          </div>
        )}
      </div>

      {/* ── Battle OTP quick-login ── */}
      {status === "battle-otp" && (
        <div className="mt-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="w-3.5 h-3.5 text-[color:var(--kl-red)]" />
            <label className="form-label mb-0">Battle OTP</label>
          </div>
          <p className="text-white/40 text-[10px] font-mono-kl uppercase tracking-widest -mt-2">
            Returning? Enter your 5-digit personal OTP to get in instantly.
          </p>
          <div className="flex gap-2 justify-between">
            {battleOtp.map((d, i) => (
              <input
                key={i}
                ref={(el) => (boRefs.current[i] = el)}
                data-testid={`p${idx}-botp-${i}`}
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => handleBoChange(i, e.target.value)}
                onKeyDown={(e) => handleBoKey(i, e)}
                onPaste={i === 0 ? handleBoPaste : undefined}
                className="otp-dark flex-1"
              />
            ))}
          </div>
          <button onClick={loginWithBattleOtp} disabled={logging || battleOtp.join("").length < 5} className="btn-submit w-full justify-center disabled:opacity-40">
            {logging ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Verifying…</> : <>Sign In <Zap className="w-4 h-4" /></>}
          </button>

          {/* Resend OTP inline */}
          <div className="mt-3">
            <button
              onClick={() => { setShowResend((v) => !v); setResendDone(null); setResendPhone(""); }}
              className="font-mono-kl text-[10px] uppercase tracking-widest text-[color:var(--kl-red)]/70 hover:text-[color:var(--kl-red)] transition flex items-center gap-1.5"
            >
              <Mail className="w-3 h-3" /> Don't have the code? Resend it →
            </button>
            {showResend && (
              <div className="mt-3 space-y-2">
                {resendDone ? (
                  <div className="rounded-md border border-emerald-500/30 bg-emerald-500/8 px-4 py-3">
                    {resendDone.email ? (
                      <p className="text-emerald-400 text-xs font-mono-kl">Code sent to <strong>{resendDone.email}</strong> — check your inbox.</p>
                    ) : (
                      <>
                        <p className="text-yellow-400 text-xs font-mono-kl mb-1">Dev mode — code returned directly:</p>
                        <span className="font-display text-2xl tracking-[0.3em] text-white">{resendDone.devCode}</span>
                      </>
                    )}
                    <button onClick={() => { setShowResend(false); setResendDone(null); }} className="mt-2 font-mono-kl text-[10px] uppercase tracking-widest text-white/40 hover:text-white/70">← Enter OTP</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      className="input-dark flex-1 text-sm py-2"
                      placeholder="Your registered phone"
                      value={resendPhone}
                      onChange={(e) => setResendPhone(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && resendEntryCode()}
                    />
                    <button onClick={resendEntryCode} disabled={resending}
                      className="btn-ghost-dark text-xs flex items-center gap-1 whitespace-nowrap">
                      <Mail className="w-3.5 h-3.5" />
                      {resending ? "Sending…" : "Send"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pt-1">
            <div className="flex-1 h-px bg-white/10" />
            <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white/25">New player?</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <button onClick={() => setStatus("form")} className="w-full py-2.5 rounded-md border border-white/15 text-white/50 hover:text-white hover:border-white/30 transition font-mono-kl text-[10px] uppercase tracking-widest">
            Register as new player
          </button>
        </div>
      )}

      {status === "form" && (
        <div className="mt-5 space-y-4">
          <div>
            <label className="form-label">Full Name <span className="req">*</span></label>
            <input className="input-dark" data-testid={`p${idx}-name`} value={player.name} onChange={(e) => setPlayer({ ...player, name: e.target.value })} placeholder="Player name" />
          </div>
          <div>
            <label className="form-label">Email <span className="req">*</span></label>
            <input className="input-dark" data-testid={`p${idx}-email`} type="email" value={player.email} onChange={(e) => setPlayer({ ...player, email: e.target.value })} placeholder="email@example.com" />
          </div>
          <div>
            <label className="form-label">Phone <span className="req">*</span></label>
            <input className="input-dark" data-testid={`p${idx}-phone`} value={player.phone} onChange={(e) => setPlayer({ ...player, phone: e.target.value })} placeholder="+91 98xxxxxxxx" />
          </div>
          <div>
            <label className="form-label">Gender <span className="req">*</span></label>
            <div className="flex gap-3 mt-1">
              {[{ v: "M", label: "Male" }, { v: "F", label: "Female" }].map(({ v, label }) => (
                <button key={v} type="button"
                  onClick={() => { setGender(v); setPlayer({ ...player, gender: v }); }}
                  className={`flex-1 py-2 rounded-md border text-sm font-mono-kl uppercase tracking-widest transition ${
                    (player.gender || gender) === v
                      ? "border-[color:var(--kl-red)] bg-[color:var(--kl-red)]/15 text-white"
                      : "border-white/15 text-white/50 hover:border-white/30 hover:text-white"
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <button onClick={send} disabled={sending} className="btn-submit w-full justify-center" data-testid={`p${idx}-send-otp`}>
            {sending ? "Sending…" : `Send OTP to Player ${idx}`} <ArrowUpRight className="w-4 h-4" />
          </button>

          {/* Lost entry code helper */}
          <div className="mt-4 border-t border-white/8 pt-4">
            <button
              onClick={() => { setShowResend((v) => !v); setResendDone(null); }}
              className="font-mono-kl text-[10px] uppercase tracking-widest text-white/35 hover:text-white/60 transition flex items-center gap-1.5"
            >
              <Mail className="w-3 h-3" />
              Already registered? Lost your battle code?
            </button>
            {showResend && (
              <div className="mt-3 space-y-2">
                {resendDone ? (
                  <div className="rounded-md border border-emerald-500/30 bg-emerald-500/8 px-4 py-3">
                    {resendDone.email ? (
                      <p className="text-emerald-400 text-xs font-mono-kl">
                        Code sent to <strong>{resendDone.email}</strong> — check your inbox.
                      </p>
                    ) : (
                      <>
                        <p className="text-yellow-400 text-xs font-mono-kl mb-1">Dev mode — code returned directly:</p>
                        <span className="font-display text-2xl tracking-[0.3em] text-white">{resendDone.devCode}</span>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      className="input-dark flex-1 text-sm py-2"
                      placeholder="Your registered phone"
                      value={resendPhone}
                      onChange={(e) => setResendPhone(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && resendEntryCode()}
                    />
                    <button
                      onClick={resendEntryCode}
                      disabled={resending}
                      className="btn-ghost-dark text-xs flex items-center gap-1 whitespace-nowrap"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {resending ? "Sending…" : "Email me"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {status === "otp" && (
        <div className="mt-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-white/70 text-sm">Phone: <span className="text-white">{player.phone}</span></div>
            <button onClick={resetToForm} className="font-mono-kl text-[10px] uppercase tracking-widest text-white/40 hover:text-white/70 transition flex items-center gap-1">
              ← Change details
            </button>
          </div>
          {devOtp && (
            <div className="rounded-md border border-[color:var(--kl-red)]/50 bg-[color:var(--kl-red)]/10 px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono-kl text-[10px] uppercase tracking-widest text-[color:var(--kl-red)]">OTP · {countdown}s left</span>
                <span className={`font-mono-kl text-xs px-2 py-0.5 rounded border font-bold tabular-nums ${timerBg} ${timerColor}`}>{countdown}s</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-display text-3xl tracking-[0.3em] text-white" data-testid={`p${idx}-dev-otp`}>{devOtp}</span>
                <button onClick={copyOtp} className="flex items-center gap-1 px-2 py-1.5 rounded border border-white/20 text-white/70 hover:text-white transition text-xs font-mono-kl">
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              {countdown === 0 && (
                <button onClick={resendOtp} disabled={sending} className="mt-2 flex items-center gap-1.5 text-[color:var(--kl-red)] text-xs font-mono-kl uppercase tracking-widest hover:opacity-80 transition">
                  <RefreshCw className="w-3 h-3" /> New OTP
                </button>
              )}
            </div>
          )}
          <div className="flex gap-1.5 justify-between">
            {otp.map((d, i) => (
              <input key={i} ref={(el) => (refs.current[i] = el)} data-testid={`p${idx}-otp-${i}`}
                className="otp-dark" style={{ width: 44, height: 54, fontSize: "1.25rem" }}
                maxLength={1} value={d} onChange={(e) => handleChange(i, e.target.value)} onPaste={handlePaste} />
            ))}
          </div>
          <button onClick={verify} disabled={verifying} className="btn-submit w-full justify-center" data-testid={`p${idx}-verify`}>
            {verifying ? "…" : "Verify"}
          </button>
        </div>
      )}

      {status === "avatar" && (
        <div className="mt-4 space-y-4">
          {/* Player confirmed */}
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-2xl text-white uppercase">{player.name}</div>
              <div className="font-mono-kl text-[9px] text-white/40 uppercase tracking-widest">{player.phone}</div>
            </div>
            <button onClick={resetToForm} className="font-mono-kl text-[9px] uppercase tracking-widest text-white/30 hover:text-white/60 transition">← Change</button>
          </div>

          {/* Gender selector (for returning users who didn't fill the form) */}
          {!gender && (
            <div>
              <div className="font-mono-kl text-[10px] uppercase tracking-widest text-white/50 mb-2">Select your gender</div>
              <div className="flex gap-2">
                {[{ v: "M", label: "Male" }, { v: "F", label: "Female" }].map(({ v, label }) => (
                  <button key={v} onClick={() => { setGender(v); setSelectedAvatar(null); }}
                    className={`flex-1 py-2 rounded-md border text-xs font-mono-kl uppercase tracking-widest transition ${
                      gender === v ? "border-[color:var(--kl-red)] bg-[color:var(--kl-red)]/15 text-white" : "border-white/15 text-white/50 hover:border-white/30 hover:text-white"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Avatar grid */}
          {gender && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono-kl text-[10px] uppercase tracking-widest text-white/50">Pick your avatar</span>
                <button onClick={() => setSelectedAvatar(getRandomAvatar(gender))}
                  className="font-mono-kl text-[9px] uppercase tracking-widest text-[color:var(--kl-red)]/70 hover:text-[color:var(--kl-red)] transition">
                  🎲 Pick for me
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {(AVATAR_OPTIONS[gender] || []).map((av) => (
                  <button key={av.id} onClick={() => setSelectedAvatar(av)}
                    className="flex flex-col items-center gap-1 transition-all"
                    style={{
                      outline: selectedAvatar?.id === av.id ? "2px solid var(--kl-red)" : "2px solid transparent",
                      borderRadius: 8,
                      transform: selectedAvatar?.id === av.id ? "scale(1.06)" : "scale(1)",
                      background: "transparent",
                    }}
                  >
                    <div style={{ width: "100%", aspectRatio: "3/4", borderRadius: 8, overflow: "hidden", position: "relative" }}>
                      <img src={av.img} alt={av.label}
                        style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", display: "block" }} />
                    </div>
                    <span className="font-mono-kl text-[8px] uppercase tracking-widest text-white/60 pb-1">{av.label}</span>
                  </button>
                ))}
              </div>
              {selectedAvatar && (
                <div className="mt-1 flex items-center gap-2 text-xs text-white/50 font-mono-kl">
                  <img src={selectedAvatar.img} alt={selectedAvatar.label} className="w-5 h-5 rounded object-cover object-top" />
                  {selectedAvatar.label} selected
                </div>
              )}
            </div>
          )}

          {/* Battle Entry Code */}
          {entryCode && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/8 px-3 py-2 flex items-center justify-between">
              <div>
                <div className="font-mono-kl text-[9px] uppercase tracking-widest text-emerald-400">Battle OTP</div>
                <span className="font-display text-xl tracking-[0.2em] text-white">{entryCode}</span>
              </div>
              <button onClick={copyEntryCode} className="flex items-center gap-1 px-2 py-1 rounded border border-white/20 text-white/60 hover:text-white transition text-xs font-mono-kl">
                {entryCopied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                Copy
              </button>
            </div>
          )}

          <button onClick={join} disabled={joining || !gender}
            className="btn-submit w-full justify-center disabled:opacity-40" data-testid={`p${idx}-join-battle`}>
            {joining ? "Joining…" : "Join the Battle"} <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {status === "waiting" && (
        <div className="mt-5 text-center" data-testid={`p${idx}-waiting`}>
          <div className="font-display text-2xl text-white uppercase mt-1">{player.name}</div>
          <div className="font-mono-kl text-[10px] uppercase tracking-widest text-white/50">In the war room</div>
          <div className="mt-4 relative w-20 h-20 mx-auto">
            <div className="absolute inset-0 rounded-full border-4 border-[color:var(--kl-red)] border-t-transparent animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center font-display text-xl text-white">{elapsed}s</div>
          </div>
          <div className="text-[color:var(--kl-red)] font-mono-kl text-[10px] uppercase tracking-widest mt-3">Waiting for opponent…</div>
        </div>
      )}

      {status === "waiting_admin" && (
        <div className="mt-5 space-y-4" data-testid={`p${idx}-waiting-admin`}>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
            <span className="font-mono-kl text-[10px] uppercase tracking-widest text-yellow-400">Registered</span>
          </div>
          <div className="font-display text-2xl text-white uppercase">{player.name}</div>

          {matchInfo?.match_num ? (
            <div className="rounded-md border border-[color:var(--kl-red)]/40 p-4" style={{ background: "rgba(230,57,70,0.06)" }}>
              <div className="font-mono-kl text-[9px] uppercase tracking-widest text-[color:var(--kl-red)] mb-2">Your Match</div>
              {/* Tournament name — prominent, above match number */}
              {matchInfo.tournament_name && (
                <div className="font-mono-kl text-xs uppercase tracking-widest text-white/70 mb-1">
                  🏆 {matchInfo.tournament_name}
                </div>
              )}
              <div className="font-display text-4xl text-white leading-none">MATCH #{matchInfo.match_num}</div>
              {matchInfo.opponent_name ? (
                <div className="text-white/60 font-mono-kl text-xs uppercase tracking-widest mt-2">
                  vs. <span className="text-white font-bold">{matchInfo.opponent_name}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-white/30 font-mono-kl text-[9px] uppercase tracking-widest mt-2">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Waiting for opponent to join this slot…
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-md border border-yellow-400/25 p-3 flex items-start gap-3"
              style={{ background: "rgba(250,204,21,0.05)" }}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: "rgba(250,204,21,0.15)" }}>
                <Clock className="w-3.5 h-3.5 text-yellow-400" />
              </div>
              <div>
                <div className="font-mono-kl text-[9px] uppercase tracking-widest text-yellow-400 mb-1">You're Registered!</div>
                <p className="text-white/60 text-xs leading-relaxed">
                  Head to the <strong className="text-white">Proctorly booth</strong>. Admin will assign you to a match — this screen updates automatically.
                </p>
              </div>
            </div>
          )}

          {/* Tournament LIVE banner */}
          {matchInfo?.tournament_live && matchInfo?.opponent_name ? (
            <div className="rounded-md border border-[color:var(--kl-red)]/60 p-3 animate-pulse"
              style={{ background: "rgba(230,57,70,0.12)" }}>
              <div className="font-mono-kl text-[9px] uppercase tracking-widest text-[color:var(--kl-red)] mb-1 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[color:var(--kl-red)] animate-pulse" />
                🔴 Tournament is LIVE!
              </div>
              <p className="text-white/80 text-xs font-medium">Please come to the <strong className="text-white">Kiba Labs booth</strong> right now. Your match is about to be called by the admin!</p>
            </div>
          ) : matchInfo?.opponent_name ? (
            <div className="rounded-md border border-yellow-400/20 bg-yellow-400/5 p-3">
              <div className="font-mono-kl text-[9px] uppercase tracking-widest text-yellow-400 mb-1">Paired! Waiting for admin</div>
              <p className="text-white/60 text-xs">Please keep a watch on the <strong className="text-white">Proctorly booth</strong> for your slot time. All The Proctorly Best!</p>
            </div>
          ) : null}

          <div className="flex items-center gap-2 text-white/30 font-mono-kl text-[9px]">
            <Loader2 className="w-3 h-3 animate-spin shrink-0" />
            Live status updates every 1.5s…
          </div>
        </div>
      )}

      {status === "matched" && (
        <div className="mt-5 text-center" data-testid={`p${idx}-matched`}>
          <div className="font-display text-2xl text-white uppercase">{player.name}</div>
          <Loader2 className="w-7 h-7 animate-spin mx-auto mt-3 text-[color:var(--kl-red)]" />
          <div className="text-[color:var(--kl-red)] font-mono-kl text-[10px] uppercase tracking-widest mt-2">Match found! Entering battle…</div>
        </div>
      )}
    </div>
  );
}

export default function BattleRegistrationModal({ open, onClose }) {
  const navigate = useNavigate();
  const [showInstructions, setShowInstructions] = useState(true);
  const [p1, setP1] = useState({ name: "", email: "", phone: "" });
  const [p2, setP2] = useState({ name: "", email: "", phone: "" });
  const [s1, setS1] = useState("battle-otp");
  const [s2, setS2] = useState("battle-otp");
  const [j1, setJ1] = useState(null);
  const [j2, setJ2] = useState(null);
  const navigatedRef = useRef(false);

  // Battle experience settings — chosen before the battle starts
  const [battleMode, setBattleMode] = useState("ai_voice");   // "live_avatar" | "ai_voice"
  const [agentGender, setAgentGender] = useState("female");
  const [p1Lang, setP1Lang] = useState("en");
  const [p2Lang, setP2Lang] = useState("en");

  // Quick Battle state
  const [qbOtp, setQbOtp] = useState(["", "", "", "", "", ""]);
  const [qbSent, setQbSent] = useState(false);
  const [qbSending, setQbSending] = useState(false);
  const [qbStarting, setQbStarting] = useState(false);
  const [qbShowInstructions, setQbShowInstructions] = useState(false);
  const [p1MatchNum, setP1MatchNum] = useState(null);
  const [p2MatchNum, setP2MatchNum] = useState(null);
  const qbRefs = useRef([]);

  useEffect(() => {
    if (!open) {
      setP1({ name: "", email: "", phone: "" });
      setP2({ name: "", email: "", phone: "" });
      setS1("battle-otp"); setS2("battle-otp");
      setJ1(null); setJ2(null);
      navigatedRef.current = false;
      setShowInstructions(true); // reset for next open
      setQbOtp(["", "", "", "", "", ""]);
      setQbSent(false);
      setQbShowInstructions(false);
      setP1MatchNum(null);
      setP2MatchNum(null);
    }
  }, [open]);

  const onMatch = (r, matchedAvatar) => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    // r may come from tournament self-register (battle_info) or legacy queue
    const myRole = r.role;
    const av1 = myRole === "player1" ? (matchedAvatar || p1.avatar || DEFAULT_AVATAR) : (p1.avatar || DEFAULT_AVATAR);
    const av2 = myRole === "player2" ? (matchedAvatar || p2.avatar || DEFAULT_AVATAR) : (p2.avatar || DEFAULT_AVATAR);
    sessionStorage.setItem("proctorly_battle", JSON.stringify({
      player1: { ...(r.player1 || {}), avatar: av1 },
      player2: { ...(r.player2 || {}), avatar: av2 },
      room_id: r.room_id,
      role: r.role,
      tournament: r.tournament || null,
      battleMode,
      agentGender,
      p1Lang,
      p2Lang,
    }));
    setTimeout(() => navigate("/battle"), 800);
  };

  const handleGenerateQbOtp = async () => {
    setQbSending(true);
    try {
      const r = await api.adminGenerateQuickBattleOtp();
      setQbSent(true);
      toast.success(`Admin OTP generated! ${r.emailed_to?.length ? `Sent to ${r.emailed_to.length} admin(s).` : `Code: ${r.otp}`}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to generate code. Make sure you are logged in as admin.";
      toast.error(msg);
    } finally { setQbSending(false); }
  };

  const handleQbOtpChange = (i, v) => {
    const ch = v.replace(/\D/g, "").slice(-1);
    const next = [...qbOtp]; next[i] = ch; setQbOtp(next);
    if (ch && i < 5) qbRefs.current[i + 1]?.focus();
  };
  const handleQbOtpKey = (i, e) => {
    if (e.key === "Backspace" && !qbOtp[i] && i > 0) qbRefs.current[i - 1]?.focus();
  };
  const handleQbOtpPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!txt) return; e.preventDefault();
    const arr = ["", "", "", "", "", ""];
    for (let i = 0; i < txt.length; i++) arr[i] = txt[i];
    setQbOtp(arr);
    qbRefs.current[Math.min(txt.length, 5)]?.focus();
  };

  const handleStartQuickBattle = async () => {
    const code = qbOtp.join("");
    if (code.length < 6) { toast.error("Enter the full 6-digit admin code"); return; }
    if (!p1.phone || !p2.phone) { toast.error("Both players must be registered first"); return; }
    setQbStarting(true);
    try {
      const room = await api.battleQuickStart(p1.phone, p2.phone, code);
      toast.success("Quick Battle unlocked! Showing instructions…");
      if (navigatedRef.current) return;
      // Store room data WITHOUT battleMode — Battle.jsx will run full mode+topic setup
      sessionStorage.setItem("proctorly_battle", JSON.stringify({
        player1: { name: room.player1.name, email: room.player1.email, phone: room.player1.phone, avatar: p1.avatar || room.player1.avatar || DEFAULT_AVATAR },
        player2: { name: room.player2.name, email: room.player2.email, phone: room.player2.phone, avatar: p2.avatar || room.player2.avatar || DEFAULT_AVATAR },
        room_id: room.room_id,
        role: "player1",
        tournament: null,
        // battleMode intentionally omitted — Battle.jsx will handle mode/lang/topic setup
      }));
      // Show instruction modal before navigating
      setQbShowInstructions(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to start Quick Battle";
      toast.error(msg);
    } finally { setQbStarting(false); }
  };

  if (!open) return null;

  // Show instruction video first — registration appears only after dismissal
  if (showInstructions) {
    return (
      <InstructionModal
        mode="battle"
        initialLang="random"
        onStart={() => setShowInstructions(false)}
      />
    );
  }

  // Quick Battle instruction interception: show instructions again before entering the battle
  if (qbShowInstructions) {
    return (
      <InstructionModal
        mode="battle"
        initialLang="random"
        onStart={() => {
          setQbShowInstructions(false);
          navigatedRef.current = true;
          navigate("/battle");
        }}
      />
    );
  }

  const ALL_BG_AVATARS = [
    "/24-girl.png", "/25-girl.png", "/26-girl.png", "/34-girl.png", "/35-girl.png", "/36-girl.png", "/38-girl.png",
    "/27-boy.png",  "/28-boy.png",  "/29-boy.png",  "/30-boy.png",  "/31-boy.png",  "/32-boy.png",  "/33-boy.png", "/37-boy.png",
    "/fe-1.png",    "/fe-2.png",    "/fe-3.png",    "/fe-4.png",
    "/male-1.png",  "/male-2.png",  "/male-3.png",  "/male-4.png",
  ];
  const BG_COLS = [
    { dir: "up",   speed: 22, delay: "0s",   opacity: 0.75, blur: 0, gs: 55, bri: 0.55 },
    { dir: "down", speed: 29, delay: "2.1s", opacity: 0.65, blur: 0, gs: 50, bri: 0.50 },
    { dir: "up",   speed: 17, delay: "0.5s", opacity: 0.50, blur: 0, gs: 48, bri: 0.44 },
    { dir: "down", speed: 33, delay: "1.1s", opacity: 0.34, blur: 1, gs: 42, bri: 0.38 },
    { dir: "up",   speed: 25, delay: "0s",   opacity: 0.22, blur: 3, gs: 38, bri: 0.32 },
    { dir: "down", speed: 20, delay: "1.6s", opacity: 0.22, blur: 3, gs: 38, bri: 0.32 },
    { dir: "up",   speed: 31, delay: "0.3s", opacity: 0.34, blur: 1, gs: 42, bri: 0.38 },
    { dir: "down", speed: 18, delay: "2.6s", opacity: 0.50, blur: 0, gs: 48, bri: 0.44 },
    { dir: "up",   speed: 26, delay: "0.8s", opacity: 0.65, blur: 0, gs: 50, bri: 0.50 },
    { dir: "down", speed: 23, delay: "1.3s", opacity: 0.75, blur: 0, gs: 55, bri: 0.55 },
  ];
  const getBgCol = (ci) => {
    const imgs = [];
    for (let i = 0; i < 4; i++) imgs.push(ALL_BG_AVATARS[(ci * 3 + i) % ALL_BG_AVATARS.length]);
    return [...imgs, ...imgs];
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "var(--kl-navy-deep)" }}
      data-testid="battle-register-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* ── Cinematic "Wall of Fighters" background ── */}
      <style>{`
        @keyframes brm-up   { 0%  { transform: translateY(0);    } 100% { transform: translateY(-50%); } }
        @keyframes brm-down { 0%  { transform: translateY(-50%); } 100% { transform: translateY(0);    } }
        @keyframes brm-glitch { 0%,95%,100%{opacity:1;transform:translateX(0)}
          96%{opacity:.7;transform:translateX(-3px)} 97%{opacity:.8;transform:translateX(3px)}
          98%{opacity:1;transform:translateX(0)} }
      `}</style>
      <div style={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden", pointerEvents: "none" }}>
        {BG_COLS.map((col, ci) => (
          <div key={ci} style={{
            position: "absolute", left: `${ci * 10}%`, top: 0, bottom: 0, width: "10.4%",
            overflow: "hidden", opacity: col.opacity,
            filter: col.blur > 0 ? `blur(${col.blur}px)` : undefined,
          }}>
            <div style={{
              display: "flex", flexDirection: "column", gap: 6,
              animation: `${col.dir === "up" ? "brm-up" : "brm-down"} ${col.speed}s linear ${col.delay} infinite`,
            }}>
              {getBgCol(ci).map((src, ii) => (
                <img key={ii} src={src} alt="" style={{
                  width: "100%", height: "27vh", objectFit: "cover", objectPosition: "top center",
                  borderRadius: 8, flexShrink: 0,
                  filter: `grayscale(${col.gs}%) brightness(${col.bri}) contrast(1.2)`,
                  animation: (ci === 0 || ci === 9) && ii % 3 === 0
                    ? `brm-glitch ${8 + ci}s ease-in-out ${ii * 1.2}s infinite` : undefined,
                }} />
              ))}
            </div>
          </div>
        ))}
        {/* Side fades */}
        <div style={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(90deg, rgba(6,10,31,0.92) 0%, transparent 18%, transparent 82%, rgba(6,10,31,0.92) 100%)" }} />
        {/* Top/bottom fades */}
        <div style={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(180deg, rgba(6,10,31,0.95) 0%, transparent 12%, transparent 88%, rgba(6,10,31,0.95) 100%)" }} />
        {/* Centre dark stage for modal to float on */}
        <div style={{ position: "absolute", inset: 0, zIndex: 2, background: "radial-gradient(ellipse 70% 80% at 50% 50%, rgba(6,10,31,0.88) 0%, rgba(6,10,31,0.55) 55%, transparent 100%)" }} />
        {/* Red arena glow */}
        <div style={{ position: "absolute", bottom: -80, left: "50%", transform: "translateX(-50%)", width: "70%", height: 200, zIndex: 2, background: "radial-gradient(ellipse, rgba(230,57,70,0.18) 0%, transparent 70%)", filter: "blur(24px)" }} />
      </div>
      <div
        className="form-dark w-full max-w-5xl rounded-md border border-[color:var(--kl-navy-line)] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] slide-up overflow-hidden max-h-[90vh] overflow-y-auto relative z-10"
        style={{ background: "linear-gradient(180deg,rgba(10,15,44,0.97) 0%, rgba(6,10,31,0.97) 100%)" }}
      >
        <div className="flex items-center justify-between px-7 py-5 border-b border-[color:var(--kl-navy-line)] sticky top-0 z-10" style={{ background: "rgba(6,10,31,0.9)", backdropFilter: "blur(8px)" }}>
          <div className="flex items-center gap-3">
            <span className="pill-tag text-white/80"><span className="dot" /> Proctorly · 2-Player Battle</span>
          </div>
          <button onClick={onClose} data-testid="battle-register-close" className="text-white/60 hover:text-white p-1 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-7 md:px-10 pt-7 pb-9">
          <h2 className="text-white text-3xl md:text-[2.6rem] font-bold leading-[1.05] tracking-tight">
            Battle setup with <span className="proctorly-mark">Proctorly</span>.
          </h2>
          <p className="text-white/55 mt-3 text-sm md:text-base leading-relaxed max-w-2xl">
            Each player verifies and registers. Head to the Proctorly booth — admin will assign you to a match and start when you're both ready.
          </p>

          <div className="grid md:grid-cols-2 gap-5 mt-8">
            <PlayerForm idx={1} player={p1} setPlayer={setP1} status={s1} setStatus={setS1} joinId={j1} setJoinId={setJ1} onMatch={onMatch} otherPlayerPhone={p2.phone} onMatchNumChange={(mn) => setP1MatchNum(mn)} />
            <PlayerForm idx={2} player={p2} setPlayer={setP2} status={s2} setStatus={setS2} joinId={j2} setJoinId={setJ2} onMatch={onMatch} otherPlayerPhone={p1.phone} onMatchNumChange={(mn) => setP2MatchNum(mn)} />
          </div>

          {/* ── Quick Battle ─────────────────────────────────────────────── */}
          {(() => {
            const bothReady = s1 === "waiting_admin" && s2 === "waiting_admin";
            const inTournament = !!(p1MatchNum || p2MatchNum);
            const qbCode = qbOtp.join("");
            if (!bothReady && !inTournament) return null; // hidden until both registered
            return (
              <div className={`mt-6 rounded-2xl overflow-hidden transition-all ${inTournament ? "opacity-35 pointer-events-none" : ""}`}
                style={{ border: `1.5px solid ${inTournament ? "rgba(255,255,255,0.08)" : "rgba(250,204,21,0.4)"}`, background: inTournament ? "rgba(255,255,255,0.02)" : "rgba(250,204,21,0.06)" }}>

                {/* Header bar */}
                <div className="flex items-center justify-between px-6 py-4 border-b"
                  style={{ borderColor: inTournament ? "rgba(255,255,255,0.06)" : "rgba(250,204,21,0.2)", background: inTournament ? "rgba(255,255,255,0.02)" : "rgba(250,204,21,0.08)" }}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: "rgba(250,204,21,0.15)", border: "1px solid rgba(250,204,21,0.35)" }}>
                      <Zap className="w-4 h-4 text-yellow-400" />
                    </div>
                    <div>
                      <div className="font-display text-lg uppercase tracking-wide text-white">Quick Battle</div>
                      <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40 mt-0.5">
                        Instant · no tournament · admin OTP required · prize if 7+/10
                      </div>
                    </div>
                  </div>
                  {inTournament && (
                    <span className="font-mono-kl text-[9px] uppercase tracking-widest px-3 py-1 rounded-full text-white/35 border border-white/10">
                      Disabled — tournament assigned
                    </span>
                  )}
                </div>

                {/* 3-step action row */}
                <div className="p-6">
                  <div className="grid md:grid-cols-3 gap-4 items-start">

                    {/* Step 1 — Generate code */}
                    <div className="rounded-xl p-4 flex flex-col gap-3"
                      style={{ background: "rgba(250,204,21,0.05)", border: "1px solid rgba(250,204,21,0.15)" }}>
                      <div className="font-mono-kl text-[9px] uppercase tracking-[0.3em] text-yellow-400/60">Step 1</div>
                      <div className="text-white/70 text-xs font-mono-kl leading-relaxed">
                        Admin clicks below → OTP emailed to all admins → admin shares the code
                      </div>
                      <button
                        onClick={handleGenerateQbOtp}
                        disabled={qbSending}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-display text-sm uppercase tracking-widest transition-all"
                        style={qbSent
                          ? { background: "rgba(74,222,128,0.10)", border: "1.5px solid rgba(74,222,128,0.4)", color: "#4ade80" }
                          : { background: "rgba(250,204,21,0.12)", border: "1.5px solid rgba(250,204,21,0.4)", color: "#facc15" }
                        }
                      >
                        {qbSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                        {qbSent ? "Code Sent ✓" : "Generate Admin Code"}
                      </button>
                    </div>

                    {/* Step 2 — Enter OTP */}
                    <div className="rounded-xl p-4 flex flex-col gap-3"
                      style={{ background: "rgba(250,204,21,0.05)", border: "1px solid rgba(250,204,21,0.15)" }}>
                      <div className="font-mono-kl text-[9px] uppercase tracking-[0.3em] text-yellow-400/60">Step 2</div>
                      <div className="text-white/70 text-xs font-mono-kl leading-relaxed">
                        Enter the 6-digit code the admin shares from their email
                      </div>
                      <div className="flex gap-1.5">
                        {qbOtp.map((d, i) => (
                          <input
                            key={i}
                            ref={(el) => (qbRefs.current[i] = el)}
                            inputMode="numeric"
                            maxLength={1}
                            value={d}
                            onChange={(e) => handleQbOtpChange(i, e.target.value)}
                            onKeyDown={(e) => handleQbOtpKey(i, e)}
                            onPaste={i === 0 ? handleQbOtpPaste : undefined}
                            className="otp-dark"
                            style={{ flex: 1, minWidth: 0, maxWidth: 44 }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Step 3 — Start */}
                    <div className="rounded-xl p-4 flex flex-col gap-3"
                      style={{ background: "rgba(250,204,21,0.05)", border: "1px solid rgba(250,204,21,0.15)" }}>
                      <div className="font-mono-kl text-[9px] uppercase tracking-[0.3em] text-yellow-400/60">Step 3</div>
                      <div className="text-white/70 text-xs font-mono-kl leading-relaxed">
                        Both players are ready. Hit the button and the battle begins!
                      </div>
                      <button
                        onClick={handleStartQuickBattle}
                        disabled={qbStarting || qbCode.length < 6}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-display text-sm uppercase tracking-widest transition-all"
                        style={qbCode.length === 6
                          ? { background: "rgba(250,204,21,0.18)", border: "2px solid #facc15", color: "#facc15", boxShadow: "0 0 20px rgba(250,204,21,0.15)" }
                          : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.2)" }
                        }
                      >
                        {qbStarting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                        {qbStarting ? "Starting…" : "Start Quick Battle"}
                      </button>
                    </div>

                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Battle Experience Settings ───────────────────────────────── */}
          <div className="mt-8 rounded-xl border border-white/10 p-6" style={{ background: "rgba(255,255,255,0.025)" }}>
            <div className="font-mono-kl text-[10px] uppercase tracking-[0.3em] text-white/40 mb-5">
              ⚡ Battle Experience Settings
            </div>

            <div className="grid md:grid-cols-3 gap-6">

              {/* Mode */}
              <div>
                <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/35 mb-2">Avatar Mode</div>
                <div className="flex flex-col gap-2">
                  {[
                    { code: "live_avatar", label: "🎥 Live Avatar", sub: "Real-time AI face · EN only" },
                    { code: "ai_voice",    label: "🎙 AI Voice",    sub: "Hindi · Bhojpuri · English" },
                  ].map(({ code, label, sub }) => (
                    <button key={code} onClick={() => setBattleMode(code)}
                      className="text-left px-3 py-2.5 rounded-lg border transition-all"
                      style={battleMode === code
                        ? { background: "rgba(230,57,70,0.12)", border: "1.5px solid var(--kl-red)", color: "#fff" }
                        : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }
                      }>
                      <div className="font-display text-sm uppercase">{label}</div>
                      <div className="font-mono-kl text-[9px] uppercase tracking-widest opacity-60 mt-0.5">{sub}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Per-player language (AI Voice only) */}
              {battleMode === "ai_voice" ? (
                <div className="md:col-span-1">
                  <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/35 mb-2">Language per Player</div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: "P1 Language", val: p1Lang, set: setP1Lang },
                      { label: "P2 Language", val: p2Lang, set: setP2Lang },
                    ].map(({ label, val, set }) => (
                      <div key={label}>
                        <div className="font-mono-kl text-[8px] uppercase tracking-widest text-white/25 mb-1">{label}</div>
                        {[{ c: "en", l: "English" }, { c: "hi", l: "हिन्दी" }, { c: "bho", l: "भोजपुरी" }].map(({ c, l }) => (
                          <button key={c} onClick={() => set(c)}
                            className="w-full text-left px-2 py-1.5 rounded border mb-1 font-mono-kl text-[9px] uppercase tracking-widest transition-all"
                            style={val === c
                              ? { background: "rgba(230,57,70,0.12)", border: "1px solid var(--kl-red)", color: "#fff" }
                              : { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.4)" }
                            }>{l}</button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center">
                  <div className="px-4 py-3 rounded-lg font-mono-kl text-[10px] uppercase tracking-widest w-full text-center"
                    style={{ background: "rgba(230,57,70,0.08)", border: "1px solid rgba(230,57,70,0.2)", color: "var(--kl-red)" }}>
                    🎥 Live Avatar<br />
                    <span className="text-white/40 text-[9px]">English · Both players</span>
                  </div>
                </div>
              )}

              {/* Agent voice gender */}
              <div>
                <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/35 mb-2">Agent Voice</div>
                <div className="flex flex-col gap-2">
                  {[
                    { code: "female", label: "♀ Female", sub: "Female AI agent voice" },
                    { code: "male",   label: "♂ Male",   sub: "Male AI agent voice" },
                  ].map(({ code, label, sub }) => (
                    <button key={code} onClick={() => setAgentGender(code)}
                      className="text-left px-3 py-2.5 rounded-lg border transition-all"
                      style={agentGender === code
                        ? { background: "rgba(230,57,70,0.12)", border: "1.5px solid var(--kl-red)", color: "#fff" }
                        : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }
                      }>
                      <div className="font-display text-sm uppercase">{label}</div>
                      <div className="font-mono-kl text-[9px] uppercase tracking-widest opacity-60 mt-0.5">{sub}</div>
                    </button>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
